import React, { useState, useCallback } from 'react';
import resourcesService from '../services/resourcesService';

const ResourceFileUploadModal = ({ isOpen, onClose, selectedCategory, selectedCategoryName, categories }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState(selectedCategory || '');
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);

  // Update upload category when selectedCategory changes
  React.useEffect(() => {
    if (selectedCategory && !uploadCategory) {
      setUploadCategory(selectedCategory);
    }
  }, [selectedCategory, uploadCategory]);

  const handleFileSelect = useCallback((files) => {
    setSelectedFiles(Array.from(files));
    setUploadResults([]);
    setAllUploadsComplete(false);
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleUpload = async () => {
    if (!uploadCategory || selectedFiles.length === 0) {
      alert('Please select a category and at least one file to upload.');
      return;
    }

    setIsUploading(true);
    setUploadProgress({});
    setUploadResults([]);

    try {
      const results = await resourcesService.uploadFiles(selectedFiles, uploadCategory, (progress) => {
        setUploadProgress(progress);
      });

      setUploadResults(results);
      setAllUploadsComplete(true);
      
      // Count successful uploads
      const successCount = results.filter(r => r.success).length;
      const totalCount = results.length;
      
      if (successCount === totalCount) {
        alert(`All ${totalCount} files uploaded successfully!`);
      } else {
        alert(`${successCount} of ${totalCount} files uploaded successfully. Check results for details.`);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      setUploadProgress({});
      setUploadResults([]);
      setAllUploadsComplete(false);
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-cloud-upload me-2"></i>
              Upload Knowledge Base Resources
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isUploading}
            ></button>
          </div>
          <div className="modal-body">
            {/* Category Selection */}
            <div className="mb-4">
              <label htmlFor="uploadCategorySelect" className="form-label">
                <i className="bi bi-folder me-1"></i>
                Select Category
              </label>
              <select
                id="uploadCategorySelect"
                className="form-select"
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                disabled={isUploading}
              >
                <option value="">Choose a category...</option>
                {categories.map((category) => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>

            {/* File Drop Zone */}
            <div
              className="border border-dashed border-2 p-4 text-center mb-3"
              style={{ 
                borderColor: '#dee2e6',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                minHeight: '150px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <i className="bi bi-cloud-upload display-4 text-muted mb-2"></i>
              <p className="mb-2 text-muted">
                Drag and drop files here, or click to select files
              </p>
              <p className="small text-muted mb-0">
                Supported formats: PDF, DOC, DOCX, TXT
              </p>
            </div>

            <input
              type="file"
              id="fileInput"
              multiple
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt"
              disabled={isUploading}
            />

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="mb-3">
                <h6>Selected Files ({selectedFiles.length})</h6>
                <div className="list-group">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <i className="bi bi-file-earmark me-2"></i>
                        <strong>{file.name}</strong>
                        <span className="text-muted ms-2">({formatFileSize(file.size)})</span>
                      </div>
                      {!isUploading && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeFile(index)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && uploadProgress && (
              <div className="mb-3">
                <h6>Upload Progress</h6>
                <div className="mb-2">
                  <strong>Overall Progress:</strong> {uploadProgress.overallProgress || 0}%
                </div>
                {uploadProgress.currentFileName && (
                  <div className="mb-2">
                    <strong>Current File:</strong> {uploadProgress.currentFileName}
                  </div>
                )}
                <div className="progress mb-2">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${uploadProgress.overallProgress || 0}%` }}
                    aria-valuenow={uploadProgress.overallProgress || 0}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                {uploadProgress.fileProgress !== undefined && (
                  <div>
                    <strong>File Progress:</strong> {uploadProgress.fileProgress}%
                    <div className="progress">
                      <div
                        className="progress-bar bg-info"
                        role="progressbar"
                        style={{ width: `${uploadProgress.fileProgress}%` }}
                        aria-valuenow={uploadProgress.fileProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upload Results */}
            {allUploadsComplete && uploadResults.length > 0 && (
              <div className="mb-3">
                <h6>Upload Results</h6>
                <div className="list-group">
                  {uploadResults.map((result, index) => (
                    <div
                      key={index}
                      className={`list-group-item d-flex justify-content-between align-items-center ${
                        result.success ? 'list-group-item-success' : 'list-group-item-danger'
                      }`}
                    >
                      <div>
                        <i className={`bi ${result.success ? 'bi-check-circle' : 'bi-x-circle'} me-2`}></i>
                        <strong>{result.filename}</strong>
                      </div>
                      <span className={`badge ${result.success ? 'bg-success' : 'bg-danger'}`}>
                        {result.success ? 'Success' : 'Failed'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isUploading}
            >
              {allUploadsComplete ? 'Close' : 'Cancel'}
            </button>
            {!allUploadsComplete && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={isUploading || selectedFiles.length === 0 || !uploadCategory}
              >
                {isUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-upload me-2"></i>
                    Upload Files ({selectedFiles.length})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceFileUploadModal;